import { useNavigate } from "react-router-dom";
import { Title } from "../../types/Title";
import useUrlUtilities from "../../utilities/UrlUtilities";
import LoadingEllipsis from "../LoadingEllipsis";
import { FormEvent, useEffect, useState } from "react";
import useTitlesService from "../../services/TitlesService";
import NegativeButton from "../NegativeButton";
import PositiveButton from "../PositiveButton";
import SimpleAlert from "../SimpleAlert";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { BarsArrowUpIcon, MagnifyingGlassCircleIcon, UsersIcon } from "@heroicons/react/24/outline";

interface TitleDetailFormProps {
  title: Title;
  onSaved?: (title: Title) => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export default function TitleDetailForm({
  title,
  onSaved,
}: TitleDetailFormProps) {
  const [formData, setFormData] = useState({
    name: title.name,
    description: title.description,
    url: title.url,
    organisation: title.organisation,
    publisher: title.publisher,
    imageUrl: title.imageUrl,
    metaId: title.metaId,
    metaUrl: title.metaUrl,
    metaAggregateRating: title.metaAggregateRating,
    metaRatingCount: title.metaRatingCount,
    metaApplicationCategory: title.metaApplicationCategory,
    metaApplicationSubcategories: title.metaApplicationSubcategories,
    metaCategory: title.metaCategory,
    metaCreator: title.metaCreator,
    metaDescription: title.metaDescription,
    metaGamePlatforms: title.metaGamePlatforms || [],
    metaImages: title.metaImages || [],
    metaPublisher: title.metaPublisher,
    metaPrice: title.metaPrice,
    igdbId: title.igdbId,
    igdbUrl: title.igdbUrl,
    igdbSummary: title.igdbSummary,
    igdbCover: title.igdbCover,
    igdbSimilarGames: title.igdbSimilarGames || [],
    alternativeNames: title.alternativeNames || [],
    searchWeight: title.searchWeight || 0,
    genreTags: title.genreTags || [],
    themeTags: title.themeTags || [],
    knownTwitchGameId: title.knownTwitchGameId
  });

  const tabs = [
    { name: "Details", id:"details"},
    { name: "Categories", id:"categories"},
    { name: "IGDB", id:"igdb"},
    { name: "Meta", id:"meta"}
  ]

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const { isValidUrl } = useUrlUtilities();
  const navigate = useNavigate();
  const debounceDelay = 500;

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [genres, setGenres] = useState<string[]>([]);
  const [themes, setThemes] = useState<string[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const { updateTitle, deleteTitle, getIgdbGame, getMetaStoreTitle, getCategories } = useTitlesService();

  const deleteThisTitle = async () => {
    try {
      setIsDeleting(true);
      await deleteTitle(title.id);
      setDeleteAlertOpen(false);
      navigate(-1);
    } catch (error) {
      const newErrors: any = {
        name: "Failed to delete title. Please try again. If the problem persists, please contact support.",
      };
      setValidationErrors(newErrors);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors: any = {};

    if (formData.name.trim() === "") {
      newErrors.name = "Title name is required";
      isValid = false;
    }

    if ((formData.url ?? "").trim() !== "") {
      // Check the website url is a valid url using regex.
      if (!isValidUrl(formData.url!.trim())) {
        newErrors.url =
          "Url is not a valid url in the form https://company.com";
        isValid = false;
      }
    }

    setValidationErrors(newErrors);

    return isValid;
  };

  const saveTitle = async (e: FormEvent) => {
    e.preventDefault();

    setSubmitted(true);

    if (!validateForm()) return;

    setIsSaving(true);

    try {
      // Save the title
      const titleToSave: Title = {
        ...title,
        ...formData,
      };

      const newTitle = await updateTitle(titleToSave.id, titleToSave);

      if (onSaved) {
        onSaved(newTitle);
      }

      setIsClean(true);
    } catch (error) {
      const newErrors: any = {
        name: "Failed to save title. Please try again. If the problem persists, please contact support.",
      };
      setValidationErrors(newErrors);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const updateFormDataWithIgdbId = () => {
    if (!formData.igdbId) return;

    getIgdbGame(formData.igdbId)
      .then((igdbGame) => {
        if (!igdbGame) return;

        var newFormPartialData: any = {
          igdbId: igdbGame.id.toString()
        };

        if (igdbGame.similarGames) {
            // Convert the array of ints to an array of strings.
            newFormPartialData.igdbSimilarGames = igdbGame.similarGames.map(
              (game) => game.toString()
            );
        }

        if (igdbGame.cover && igdbGame.cover.imageId) {
          newFormPartialData.igdbCover = `https://images.igdb.com/igdb/image/upload/t_cover_big/${igdbGame.cover.imageId}.jpg`;
          newFormPartialData.imageUrl = newFormPartialData.igdbCover;
        }

        if (igdbGame.url) {
          newFormPartialData.igdbUrl = igdbGame.url;
        }

        if (igdbGame.summary) {
          newFormPartialData.igdbSummary = igdbGame.summary;
          newFormPartialData.description = igdbGame.summary;
        }

        if (igdbGame.name) {
          newFormPartialData.name = igdbGame.name;
        }

        if (igdbGame.websites && igdbGame.websites.length > 0) {
          const website = igdbGame.websites.find((w) => w.type?.id === 1);
          if (website && website.url) {
            newFormPartialData.url = website.url;
          }
        }

        if (igdbGame.involvedCompanies) {
          for (const company of igdbGame.involvedCompanies) {
            if (company.developer && company.company && company.company.name) {
              newFormPartialData.organisation = company.company.name;
            }
            if (company.publisher && company.company && company.company.name) {
              newFormPartialData.publisher = company.company.name;
            }
          }
        }

        const updatedFormData = {
          ...formData,
          ...newFormPartialData,
        };

        setFormData(updatedFormData);

        alert("IGDB information loaded. Changes also made in the details tab, please review before saving.");
      })
      .catch((error) => {
        console.error("Error fetching IGDB game details:", error);
      });
  };

  const updateFormDataWithMetaStoreInformation = () => {
    if (!formData.metaUrl) return;
    if (!isValidUrl(formData.metaUrl)) {
      return;
    }

    getMetaStoreTitle(formData.metaUrl)
      .then((metaStoreData) => {
        if (!metaStoreData) return;

        var newFormPartialData: any = {
          metaApplicationCategory: metaStoreData.applicationCategory,
          metaCategory: metaStoreData.category,
          metaDescription: metaStoreData.description,
          metaGamePlatforms: metaStoreData.gamePlatform || [],
          metaImages: metaStoreData.image || [],
        };

        if (metaStoreData.applicationSubCategory) {
          // Convert the array of strings to a comma-separated string.
            newFormPartialData.metaApplicationSubcategories = metaStoreData.applicationSubCategory.join(", ");
        }
        
        // Throw away the query string if it exists.
        const urlWithoutQuery = formData.metaUrl!.split("?")[0];

        // Throw away the trailing slash if it exists.
        const urlWithoutTrailingSlash = urlWithoutQuery.replace(/\/$/, "");

        // Get the last part of the URL, which should be the ID.
        const urlParts = urlWithoutTrailingSlash.split("/");

        // If it's a numeric ID, set it as the metaId.
        const lastPart = urlParts[urlParts.length - 1];

        if (!isNaN(Number(lastPart))) {
          newFormPartialData.metaId = lastPart;
        } else {
          // If it's not a numeric ID, set it as the metaId.
          newFormPartialData.metaId = lastPart;
        }

        if (metaStoreData.aggregateRating) {
          newFormPartialData.metaAggregateRating =
            metaStoreData.aggregateRating.ratingValue;
          newFormPartialData.metaRatingCount =
            metaStoreData.aggregateRating.ratingCount;
        }

        if (metaStoreData.creator) {
          newFormPartialData.metaCreator = metaStoreData.creator.name;
          if (!formData.organisation) {
            newFormPartialData.organisation = newFormPartialData.metaCreator;
          }
        }

        if (metaStoreData.publisher) {
          newFormPartialData.metaPublisher = metaStoreData.publisher.name;
            if (!formData.publisher) {
                newFormPartialData.publisher = newFormPartialData.metaPublisher;
            }
        }

        if (metaStoreData.offers && metaStoreData.offers.price) {
          newFormPartialData.metaPrice = metaStoreData.offers.price;
        }

        const updatedFormData = {
          ...formData,
          ...newFormPartialData,
        };

        setFormData(updatedFormData);
      })
      .catch((error) => {
        console.error("Error fetching IGDB game details:", error);
      });
  };

  const selectTab = (id: string) => {
    const tab = tabs.find(t => t.id === id);
    if (!tab) return;
    setSelectedTab(tab);
  };

  const isGenreChecked = (g: string) => (formData.genreTags ?? []).includes(g);

  const toggleGenre = (g: string) => {
    setIsClean(false);
    setFormData(prev => {
      const current = new Set(prev.genreTags ?? []);
      current.has(g) ? current.delete(g) : current.add(g);
      return { ...prev, genreTags: Array.from(current) };
    });
  };

  const selectAllGenres = () => {
    setIsClean(false);
    setFormData(prev => ({ ...prev, genreTags: [...genres] }));
  };

  const clearAllGenres = () => {
    setIsClean(false);
    setFormData((prev) => ({ ...prev, genreTags: [] }));
  };

  const isThemeChecked = (t: string) => (formData.themeTags ?? []).includes(t);

  const toggleTheme = (t: string) => {
    setIsClean(false);
    setFormData(prev => {
      const current = new Set(prev.themeTags ?? []);
      current.has(t) ? current.delete(t) : current.add(t);
      return { ...prev, themeTags: Array.from(current) };
    });
  };

  const selectAllThemes = () => {
    setIsClean(false);
    setFormData(prev => ({ ...prev, themeTags: [...themes] }));
  };

  const clearAllThemes = () => {
    setIsClean(false);
    setFormData((prev) => ({ ...prev, themeTags: [] }));
  };

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const categories = await getCategories();
        setGenres(categories.genres);
        setThemes(categories.themes);
      } catch (error) {
        console.error("Error fetching title categories:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    if ((genres.length === 0 || themes.length === 0 ) && !isLoadingCategories) {
      fetchCategories();
    }
  }, [getCategories, isLoadingCategories, genres.length, themes.length]);

  if (isDeleting) {
    return (
      <div className="flex items-center bg-gray-30">
        <div className="flex flex-col items-center space-y-4">
          <h1 className="text-lg text-white">
            Deleting
            <LoadingEllipsis />
          </h1>
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="grid grid-cols-1 sm:hidden">
          {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
          <select
            onChange={(e) => selectTab(e.target.value)}
            defaultValue={selectedTab.id}
            aria-label="Select a tab"
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-white dark:bg-white/5 dark:text-gray-100 dark:outline-white/10 dark:*:bg-gray-800 dark:focus:outline-white"
          >
            {tabs.map((tab) => (
              <option key={tab.name} value={tab.id}>
                {tab.name}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500 dark:fill-gray-400"
          />
        </div>
        <div className="hidden sm:block">
          <nav
            aria-label="Tabs"
            className="isolate flex divide-x divide-gray-200 rounded-lg bg-white shadow dark:divide-white/10 dark:bg-gray-800/50 dark:shadow-none dark:outline dark:outline-1 dark:-outline-offset-1 dark:outline-white/10"
          >
            {tabs.map((tab, tabIdx) => (
              <a
                key={tab.name}
                href={"#"}
                onClick={(e) => {
                  e.preventDefault();
                  selectTab(tab.id);
                }}
                aria-current={tab.id == selectedTab.id ? "page" : undefined}
                className={classNames(
                  tab.id == selectedTab.id
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white",
                  tabIdx === 0 ? "rounded-l-lg" : "",
                  tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                  "group relative min-w-0 flex-1 overflow-hidden px-4 py-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10 dark:hover:bg-white/5"
                )}
              >
                <span>{tab.name}</span>
                <span
                  aria-hidden="true"
                  className={classNames(
                    tab.id == selectedTab.id
                      ? "bg-white dark:bg-white"
                      : "bg-transparent",
                    "absolute inset-x-0 bottom-0 h-0.5"
                  )}
                />
              </a>
            ))}
          </nav>
        </div>
      </div>

      <form onSubmit={saveTitle} className="form">
        <div className="flex flex-col grid grid-cols-1 gap-6">
          <div
            className={classNames(
              selectedTab.id !== "details" ? "hidden" : "",
              "card p-8 flex flex-col gap-8"
            )}
          >
            <div className="sm:col-span-4">
              <label htmlFor="name" className="block leading-6">
                Title name:
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="text"
                  name="name"
                  placeholder="Title name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                    "name"
                  )}`}
                />
              </div>
              {validationError("name") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("name")}
                </div>
              )}
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="knownTwitchGameId" className="block leading-6">
                Known Twitch Game ID (for Twitch streaming integration):
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="text"
                  name="knownTwitchGameId"
                  placeholder="This might also be a category ID like 743 for Chess"
                  value={formData.knownTwitchGameId}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                    "knownTwitchGameId"
                  )}`}
                />
              </div>
              {validationError("knownTwitchGameId") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("knownTwitchGameId")}
                </div>
              )}
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="searchWeight" className="block leading-6">
                Search weight:
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="number"
                  name="searchWeight"
                  placeholder="Search weight"
                  value={formData.searchWeight ?? 0}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                    "searchWeight"
                  )}`}
                />
              </div>
              {validationError("name") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("name")}
                </div>
              )}
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="organisation" className="block leading-6">
                Developer:
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="text"
                  name="organisation"
                  placeholder="Title developer"
                  value={formData.organisation}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                    "organisation"
                  )}`}
                />
              </div>
              {validationError("organisation") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("organisation")}
                </div>
              )}
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="publisher" className="block leading-6">
                Publisher:
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="text"
                  name="publisher"
                  placeholder="Title publisher"
                  value={formData.publisher}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                    "publisher"
                  )}`}
                />
              </div>
              {validationError("publisher") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("publisher")}
                </div>
              )}
            </div>

            {/* Website URL */}
            <div className="sm:col-span-4">
              <label htmlFor="url" className="block leading-6">
                URL:
              </label>
              <div className="mt-2">
                <input
                  disabled={isSaving}
                  type="url"
                  name="url"
                  placeholder="https://supergamestud.io"
                  value={formData.url ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
              {validationError("url") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("url")}
                </div>
              )}
            </div>

            {/* Short description */}
            <div className="sm:col-span-4">
              <label htmlFor="description" className="block leading-6">
                Description:
              </label>
              <div className="mt-2">
                <textarea
                  rows={5}
                  disabled={isSaving}
                  placeholder="Description"
                  name="description"
                  value={formData.description ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
              {validationError("description") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("description")}
                </div>
              )}
            </div>

            {/* Image URL */}
            <div className="sm:col-span-4">
              <label htmlFor="imageUrl" className="block leading-6">
                Image URL:
              </label>
              <div className="mt-2 flex">
                {formData.imageUrl && (
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    className="h-12 rounded-l"
                  />
                )}
                <input
                  disabled={isSaving}
                  type="url"
                  name="imageUrl"
                  placeholder="https://supergamestud.io"
                  value={formData.imageUrl ?? ""}
                  onChange={handleInputChange}
                  className={classNames(
                    formData.imageUrl ? "rounded-r" : "rounded",
                    "w-full px-3 py-2 border text-black"
                  )}
                />
              </div>
              {validationError("imageUrl") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("imageUrl")}
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(
              selectedTab.id !== "categories" ? "hidden" : "",
              "card p-8 flex flex-col gap-8"
            )}
          >
            {genres.length > 0 && (
              <div>
                <label htmlFor="genreTags" className="block leading-6">
                  Genres:
                </label>

                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAllGenres}
                    disabled={isSaving || genres.length === 0}
                    className="rounded border px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-white/10"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearAllGenres}
                    disabled={
                      isSaving || (formData.genreTags?.length ?? 0) === 0
                    }
                    className="rounded border px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-white/10"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.genreTags?.length ?? 0} selected
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {genres
                    .slice() // avoid mutating state if you sort
                    .sort((a, b) => a.localeCompare(b))
                    .map((g) => {
                      const id = `genre-${g
                        .replace(/[^a-z0-9]+/gi, "-")
                        .toLowerCase()}`;
                      return (
                        <label
                          key={g}
                          htmlFor={id}
                          className="flex cursor-pointer items-center gap-2 rounded border p-2 hover:bg-gray-50 dark:hover:bg-white/5"
                        >
                          <input
                            id={id}
                            type="checkbox"
                            className="h-4 w-4"
                            checked={isGenreChecked(g)}
                            onChange={() => toggleGenre(g)}
                            disabled={isSaving}
                          />
                          <span className="text-sm">{g}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
            {themes.length > 0 && (
              <div>
                <label htmlFor="themeTags" className="block leading-6">
                  Themes:
                </label>

                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAllThemes}
                    disabled={isSaving || themes.length === 0}
                    className="rounded border px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-white/10"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearAllThemes}
                    disabled={
                      isSaving || (formData.themeTags?.length ?? 0) === 0
                    }
                    className="rounded border px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-white/10"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.themeTags?.length ?? 0} selected
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {themes
                    .slice() // avoid mutating state if you sort
                    .sort((a, b) => a.localeCompare(b))
                    .map((g) => {
                      const id = `theme-${g
                        .replace(/[^a-z0-9]+/gi, "-")
                        .toLowerCase()}`;
                      return (
                        <label
                          key={g}
                          htmlFor={id}
                          className="flex cursor-pointer items-center gap-2 rounded border p-2 hover:bg-gray-50 dark:hover:bg-white/5"
                        >
                          <input
                            id={id}
                            type="checkbox"
                            className="h-4 w-4"
                            checked={isThemeChecked(g)}
                            onChange={() => toggleTheme(g)}
                            disabled={isSaving}
                          />
                          <span className="text-sm">{g}</span>
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          <div
            className={classNames(
              selectedTab.id !== "igdb" ? "hidden" : "",
              "card p-8 flex flex-col gap-8"
            )}
          >
            {/* IGDB ID */}
            <div className="sm:col-span-4">
              <label htmlFor="igdbId" className="block leading-6">
                IGDB ID:
              </label>
              <div className="mt-2 flex">
                <input
                  disabled={isSaving}
                  type="number"
                  name="igdbId"
                  placeholder="123456"
                  value={formData.igdbId ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-l text-black"
                />
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm text-gray-900 hover:bg-gray-800 outline outline-1 -outline-offset-1 outline-gray-800 focus:relative dark:bg-white/10 dark:text-white dark:outline-gray-700"
                  onClick={updateFormDataWithIgdbId}
                >
                  <MagnifyingGlassCircleIcon
                    aria-hidden="true"
                    className="-ml-0.5 size-4 text-gray-400"
                  />
                  Lookup
                </button>
              </div>
              {validationError("igdbId") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("igdbId")}
                </div>
              )}
            </div>
            {/* IGDB Loaded details */}
            <div className="sm:col-span-4">
              <label htmlFor="igdbUrl" className="block leading-6">
                IGDB URL:
              </label>
              <div className="mt-2">
                <input
                  type="url"
                  name="igdbUrl"
                  value={formData.igdbUrl ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="igdbSummary" className="block leading-6">
                IGDB Summary:
              </label>
              <div className="mt-2">
                <textarea
                  rows={5}
                  name="igdbSummary"
                  value={formData.igdbSummary ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="igdbCover" className="block leading-6">
                IGDB Cover URL:
              </label>
              <div className="mt-2">
                <input
                  type="url"
                  name="igdbCover"
                  value={formData.igdbCover ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
          </div>
          <div
            className={classNames(
              selectedTab.id !== "meta" ? "hidden" : "",
              "card p-8 flex flex-col gap-8"
            )}
          >
            {/* Meta URL */}
            <div>
              <label htmlFor="metaUrl" className="block leading-6">
                Meta store URL:
              </label>
              <div className="mt-2 flex">
                <div className="-mr-px grid grow grid-cols-1 focus-within:relative">
                  <input
                    type="url"
                    name="metaUrl"
                    placeholder=""
                    value={formData.metaUrl ?? ""}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-l text-black"
                  />
                </div>
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm text-gray-900 hover:bg-gray-800 outline outline-1 -outline-offset-1 outline-gray-800 focus:relative dark:bg-white/10 dark:text-white dark:outline-gray-700"
                  onClick={updateFormDataWithMetaStoreInformation}
                >
                  <MagnifyingGlassCircleIcon
                    aria-hidden="true"
                    className="-ml-0.5 size-4 text-gray-400"
                  />
                  Lookup
                </button>
              </div>
              {validationError("metaUrl") && (
                <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                  {validationError("metaUrl")}
                </div>
              )}
            </div>
            {/* Meta store loaded details */}
            <div className="sm:col-span-4">
              <label htmlFor="metaId" className="block leading-6">
                Meta store ID:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="metaId"
                  value={formData.metaId ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="metaAggregateRating" className="block leading-6">
                Meta average rating:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="metaAggregateRating"
                  value={formData.metaAggregateRating ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="metaRatingCount" className="block leading-6">
                Meta rating count:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="metaRatingCount"
                  value={formData.metaRatingCount ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label
                htmlFor="metaApplicationCategory"
                className="block leading-6"
              >
                Meta application category:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="metaApplicationCategory"
                  value={formData.metaApplicationCategory ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label
                htmlFor="metaApplicationSubcategories"
                className="block leading-6"
              >
                Meta application subcategories:
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="metaApplicationSubcategories"
                  value={formData.metaApplicationSubcategories ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="metaPrice" className="block leading-6">
                Meta price:
              </label>
              <div className="mt-2">
                <input
                  type="currency"
                  name="metaPrice"
                  value={formData.metaPrice ?? ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded text-black"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Submit Button */}
        <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
          <NegativeButton
            type="button"
            disabled={isSaving}
            className={`text-red-500 py-2 px-4 rounded border-red-900 border hover:bg-red-900/25 `}
            onClick={() => setDeleteAlertOpen(true)}
          >
            Delete title
          </NegativeButton>
          <PositiveButton type="submit" disabled={isSaving || isClean}>
            {isSaving ? "Saving..." : isClean ? "Saved" : "Save changes"}
          </PositiveButton>
        </div>
      </form>

      <SimpleAlert
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title={`Are you sure you want to delete ${title.name}?`}
        message="This action cannot be undone and any stats will no longer be attributed to this title."
        confirmText={`Delete ${title.name}`}
        onConfirm={deleteThisTitle}
      />
    </>
  );
}
